import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./App.css";

// Eager load essential components, auth pages, and core app pages for better UX
import { ErrorBoundary } from './components/pages/errorBoundary.jsx';
import ProtectedRoute from './ProtectedRoute.tsx';
import Login from './components/pages/Auth/Login/Login.tsx';
import Signup from './components/pages/Auth/SignUp/SignUp.tsx';
import Forgot from './components/pages/Auth/Forgot/Forgot.tsx';
import Otp from './components/pages/Auth/Otp/Otp.tsx';
import CreatePassword from './components/pages/Auth/CreatePassword/CreatePassword.tsx';
import Subscription from './components/pages/Auth/Subscription/Subscription.tsx';
import Success from './components/pages/Auth/Success/Success.tsx';
import MyGifts from './components/pages/MyGifts/MyGifts.tsx';
import StartGift from './components/pages/Gift/StartGift.tsx';
import Profile from './components/pages/Profile/Profile.tsx';
import LegacyContactSetup from './components/pages/Profile/LegacyContactSetup.tsx';
import GiftSuccess from './components/pages/Gift/GiftSuccess/GiftSuccess.tsx';
import HelpSupport from './components/pages/HelpSupport/HelpSupport.tsx';
import PageLoader from './components/common/PageLoader/PageLoader.tsx';

// Lazy load less frequently used pages for code splitting
const MyContracts = lazy(() => import('./components/pages/MyContracts/MyContracts.tsx'));
const Settings = lazy(() => import('./components/pages/Settings/Settings.tsx'));
const GiftDetails = lazy(() => import('./components/pages/MyGifts/GiftDetails.tsx'));
const NotFound = lazy(() => import('./components/pages/Auth/pageNotFound/404notfound.tsx'));
const TermsConditions = lazy(() => import('./components/pages/TermsConditions/TermsConditions.tsx'));
const PrivacyPolicy = lazy(() => import('./components/pages/PrivacyPolicy/PrivacyPolicy.tsx'));

// Protected Route Component
const SemiProtectedRoute = () => {
  return localStorage.getItem('authToken') ? <Outlet /> : <Navigate to="/" />;
};

const PublicRoute = () => {
  const isAuthenticated = localStorage.getItem('authToken');
  
  if (isAuthenticated) {
    return <Navigate to="/my-gifts" replace />;
  }
  
  return <Outlet />;
};

function App() {
  return (
      <ErrorBoundary>
    <Router>
      <Suspense fallback={<PageLoader text="Loading page..." />}>
        <Routes>
          {/* Public Routes */}
            <Route element={<PublicRoute />}>
            <Route path="/" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/otp" element={<Otp />} />
          </Route>



          <Route element={<SemiProtectedRoute />}>
              <Route path="/create-password" element={<CreatePassword />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/success" element={<Success />} />
              <Route path="/legacy-contact" element={<LegacyContactSetup />} />
            </Route>

            {/* Fully Protected Routes (require complete user setup) */}
            <Route element={<ProtectedRoute />}>
              <Route path="/my-gifts" element={<MyGifts />} />
              <Route path="/my-gifts/:id" element={<MyGifts />} />
              <Route path="/my-gifts/create/:giftId" element={<StartGift />} />
              <Route path="/my-gifts/edit/:giftId" element={<StartGift />} />
              <Route path="/my-contacts" element={<MyGifts />} />
              <Route path="/my-contracts" element={<MyGifts />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/help-support" element={<HelpSupport />} />
              <Route path="/help-support/ticket/:ticketId" element={<HelpSupport />} />
              <Route path="/start-gift" element={<StartGift />} />
              <Route path="/gift-details" element={<GiftDetails />} />
              <Route path="/gift-success" element={<GiftSuccess />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Route>

           {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="light"
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        draggable={true}
        pauseOnHover={true}
        closeButton={true}
      />
    </Router>
    </ErrorBoundary>
  );
}

export default App;
